/**
 * Cliente de «Mensajes» del hub (docs/29 §9): los envíos salen DESDE LA CUENTA DEL USUARIO
 * (su Gmail / Outlook, o WhatsApp asistido con `wa.me`) y los avisos al equipo.
 *
 * Igual que `api/hub.ts`: habla directo al hub con `credentials:'include'` (cookie del hub,
 * Domain=.opus.com.mx; CORS de launcher) y NO usa `apiFetch` (un 401 del hub no debe cerrar la
 * sesión de la app). El usuario lo pone la sesión del hub, nunca el cuerpo.
 *
 * Errores: el hub responde `{ ok:false, code, error }`; aquí se lanzan como `MensajesError` con
 * un texto en español para el usuario (`textoDeError`).
 */
import { hubBaseUrl } from './hub.js';

export type CanalTipo = 'gmail' | 'outlook' | 'whatsapp';
export type ProveedorCorreo = 'gmail' | 'outlook';
export type EstadoDelCanal = 'conectado' | 'desconectado' | 'error' | 'no_configurado';
export type AppOrigen = 'ai' | 'ci' | 'bi' | 'li' | 'hub' | 'eko27' | 'dist';

export interface CanalDelUsuario {
  tipo: CanalTipo;
  estado: EstadoDelCanal;
  cuenta: string | null;
  conectado_at: string | null;
}

export interface LimitesDeEnvio {
  por_dia: number;
  usados_hoy: number;
  destinatarios_max: number;
  adjunto_max_mb: number;
}

export interface EstadoMensajes {
  no_leidos: number;
  canales: CanalDelUsuario[];
  limites: LimitesDeEnvio;
}

export interface Destinatario {
  nombre?: string;
  correo?: string;
  telefono?: string;
}

export interface AdjuntoBase64 {
  nombre: string;
  tipo: string;
  base64: string;
}

export interface EntradaEnviar {
  canal: 'correo' | 'whatsapp';
  proveedor?: ProveedorCorreo;
  para: Destinatario[];
  asunto?: string;
  mensaje: string;
  enlace?: { url: string; titulo: string };
  adjunto?: AdjuntoBase64;
  origen: { app: AppOrigen; ref?: string };
}

export interface AbrirWhatsApp {
  telefono: string;
  url: string;
}

export type RespuestaEnviar =
  | { ok: true; envio_id: string; estado: 'enviado' | 'en_cola'; abrir?: undefined }
  | { ok: true; envio_id: string; abrir: AbrirWhatsApp[]; estado?: undefined };

export interface MiembroDelEquipo {
  correo: string;
  nombre: string;
}

export interface EntradaCompartirEquipo {
  miembros: string[] | 'todos';
  titulo: string;
  url: string;
  app: AppOrigen;
  ref?: string;
  nota?: string;
}

/** Límites de docs/29 §8 (por si el hub no los manda). */
export const LIMITES_POR_DEFECTO: LimitesDeEnvio = {
  por_dia: 100,
  usados_hoy: 0,
  destinatarios_max: 20,
  adjunto_max_mb: 10,
};

/** Códigos de §9 más los del lado de la app (sin red, sin sesión, servicio no disponible). */
export type CodigoMensajes =
  | 'NO_CONECTADO'
  | 'LIMITE_DIA'
  | 'DEMASIADOS_DESTINATARIOS'
  | 'ADJUNTO_GRANDE'
  | 'PROVEEDOR_ERROR'
  | 'TOKEN_REVOCADO'
  | 'NO_CONFIGURADO'
  | 'SIN_SESION'
  | 'NO_DISPONIBLE'
  | 'RED'
  | 'DESCONOCIDO';

export const TEXTOS_DE_ERROR: Record<CodigoMensajes, string> = {
  NO_CONECTADO: 'Todavía no conectas esa cuenta. Conéctala en Mensajes y vuelve a intentar.',
  LIMITE_DIA: 'Llegaste al límite de envíos de hoy. Podrás enviar de nuevo mañana.',
  DEMASIADOS_DESTINATARIOS: 'Son demasiados destinatarios para un solo envío.',
  ADJUNTO_GRANDE: 'El archivo adjunto es demasiado grande.',
  PROVEEDOR_ERROR: 'Tu proveedor de correo no aceptó el envío. Intenta de nuevo en unos minutos.',
  TOKEN_REVOCADO: 'Se perdió el permiso para enviar desde tu cuenta. Vuelve a conectarla en Mensajes.',
  NO_CONFIGURADO: 'Este canal todavía no está disponible.',
  SIN_SESION: 'Tu sesión de OPUS venció. Vuelve a entrar desde opus.com.mx.',
  NO_DISPONIBLE: 'Mensajes no está disponible por ahora.',
  RED: 'No hay conexión con OPUS. Revisa tu internet y vuelve a intentar.',
  DESCONOCIDO: 'No se pudo completar el envío. Intenta de nuevo.',
};

const CODIGOS = new Set(Object.keys(TEXTOS_DE_ERROR));

export class MensajesError extends Error {
  readonly code: CodigoMensajes;
  readonly status: number;
  /** Texto del servidor (para la bitácora; al usuario se le muestra `message`). */
  readonly detalle: string | null;
  constructor(code: CodigoMensajes, status = 0, detalle: string | null = null) {
    super(TEXTOS_DE_ERROR[code]);
    this.name = 'MensajesError';
    this.code = code;
    this.status = status;
    this.detalle = detalle;
  }
}

/** Texto para el usuario de cualquier error que salga de este cliente. */
export function textoDeError(e: unknown): string {
  if (e instanceof MensajesError) return e.message;
  return TEXTOS_DE_ERROR.DESCONOCIDO;
}

/** Código de §9 que manda el hub o, si no viene, uno por el estado HTTP. */
export function codigoDeRespuesta(status: number, body: unknown): CodigoMensajes {
  const raw = body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined;
  if (typeof raw === 'string' && CODIGOS.has(raw)) return raw as CodigoMensajes;
  if (status === 401 || status === 403) return 'SIN_SESION';
  if (status === 404 || status === 503 || status === 502) return 'NO_DISPONIBLE';
  if (status === 413) return 'ADJUNTO_GRANDE';
  if (status === 429) return 'LIMITE_DIA';
  return 'DESCONOCIDO';
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export function urlDeMensajes(ruta: string): string {
  return `${hubBaseUrl()}/api/client/mensajes${ruta}`;
}

async function pedir<T>(ruta: string, init: RequestInit = {}, fetchImpl: FetchLike = fetch): Promise<T> {
  let res: Response;
  try {
    res = await fetchImpl(urlDeMensajes(ruta), {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
  } catch (e) {
    if (init.signal?.aborted) throw e;
    throw new MensajesError('RED');
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const okFalse = !!body && typeof body === 'object' && (body as { ok?: unknown }).ok === false;
  if (!res.ok || okFalse) {
    const detalle =
      body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;
    throw new MensajesError(codigoDeRespuesta(res.status, body), res.status, detalle);
  }
  return body as T;
}

const num = (v: unknown, def: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : def);
const TIPOS: CanalTipo[] = ['gmail', 'outlook', 'whatsapp'];
const ESTADOS: EstadoDelCanal[] = ['conectado', 'desconectado', 'error', 'no_configurado'];

/** Respuesta de /estado a prueba de campos faltantes. */
export function normalizarEstado(raw: unknown): EstadoMensajes {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const lim = (r.limites && typeof r.limites === 'object' ? r.limites : {}) as Record<string, unknown>;
  const canales = (Array.isArray(r.canales) ? r.canales : [])
    .map((c): CanalDelUsuario | null => {
      const o = (c && typeof c === 'object' ? c : {}) as Record<string, unknown>;
      const tipo = String(o.tipo ?? '') as CanalTipo;
      if (!TIPOS.includes(tipo)) return null;
      const estado = ESTADOS.includes(o.estado as EstadoDelCanal) ? (o.estado as EstadoDelCanal) : 'desconectado';
      return {
        tipo,
        estado,
        cuenta: typeof o.cuenta === 'string' && o.cuenta ? o.cuenta : null,
        conectado_at: typeof o.conectado_at === 'string' ? o.conectado_at : null,
      };
    })
    .filter((c): c is CanalDelUsuario => c !== null);
  return {
    no_leidos: Math.max(0, Math.floor(num(r.no_leidos, 0))),
    canales,
    limites: {
      por_dia: num(lim.por_dia, LIMITES_POR_DEFECTO.por_dia),
      usados_hoy: num(lim.usados_hoy, 0),
      destinatarios_max: num(lim.destinatarios_max, LIMITES_POR_DEFECTO.destinatarios_max),
      adjunto_max_mb: num(lim.adjunto_max_mb, LIMITES_POR_DEFECTO.adjunto_max_mb),
    },
  };
}

/** Proveedores de correo conectados (para «Enviar desde mi correo»). */
export function correosConectados(estado: Pick<EstadoMensajes, 'canales'> | null): CanalDelUsuario[] {
  return (estado?.canales ?? []).filter(
    (c) => (c.tipo === 'gmail' || c.tipo === 'outlook') && c.estado === 'conectado',
  );
}

export async function obtenerEstado(signal?: AbortSignal, fetchImpl?: FetchLike): Promise<EstadoMensajes> {
  return normalizarEstado(await pedir<unknown>('/estado', { signal }, fetchImpl));
}

export async function enviarMensaje(entrada: EntradaEnviar, fetchImpl?: FetchLike): Promise<RespuestaEnviar> {
  const r = await pedir<Record<string, unknown>>(
    '/enviar',
    { method: 'POST', body: JSON.stringify(entrada) },
    fetchImpl,
  );
  const envio_id = String(r.envio_id ?? '');
  if (Array.isArray(r.abrir)) {
    const abrir = r.abrir
      .map((a) => (a && typeof a === 'object' ? (a as Record<string, unknown>) : {}))
      .filter((a) => typeof a.url === 'string' && /^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(a.url as string))
      .map((a) => ({ telefono: String(a.telefono ?? ''), url: String(a.url) }));
    return { ok: true, envio_id, abrir };
  }
  return { ok: true, envio_id, estado: r.estado === 'en_cola' ? 'en_cola' : 'enviado' };
}

export async function obtenerEquipo(fetchImpl?: FetchLike): Promise<MiembroDelEquipo[]> {
  const r = await pedir<{ miembros?: unknown }>('/equipo', {}, fetchImpl);
  return (Array.isArray(r.miembros) ? r.miembros : [])
    .map((m) => (m && typeof m === 'object' ? (m as Record<string, unknown>) : {}))
    .filter((m) => typeof m.correo === 'string' && m.correo)
    .map((m) => ({ correo: String(m.correo), nombre: String(m.nombre ?? '') }));
}

export async function compartirConEquipo(
  entrada: EntradaCompartirEquipo,
  fetchImpl?: FetchLike,
): Promise<{ avisados: number }> {
  const r = await pedir<{ avisados?: unknown }>(
    '/compartir-equipo',
    { method: 'POST', body: JSON.stringify(entrada) },
    fetchImpl,
  );
  return { avisados: num(r.avisados, 0) };
}

/**
 * Baja un archivo de ESTA app (cookie de la app) para adjuntarlo a un envío: p. ej. el PDF del
 * resumen ejecutivo de «Mis archivos». Lanza si no responde 200.
 */
export async function descargarParaAdjuntar(
  url: string,
  nombre: string,
  fetchImpl: FetchLike = fetch,
): Promise<{ nombre: string; blob: Blob }> {
  const res = await fetchImpl(url, { credentials: 'include' });
  if (!res.ok) throw new Error('No se pudo preparar el archivo.');
  const blob = await res.blob();
  const conExt = /\.pdf$/i.test(nombre) || blob.type !== 'application/pdf' ? nombre : `${nombre}.pdf`;
  return { nombre: conExt, blob };
}
